/**
 * SWISS School Portal — contextual sub-navigation
 * ===============================================
 * Renders the active section's pages. Desktop: a 220px left rail.
 * Mobile: the same list as a horizontally scrollable chip row under
 * the top bar. One component, one source of truth — the breakpoint
 * only changes the presentation.
 *
 * It renders nothing at all for leaf sections (Dashboard, Resources)
 * and for routes outside the nav model, which is what keeps the home
 * screen free of an empty rail.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { schoolHasModule } from '../services/tiers';
import './SubRail.css';

const LOCKED_HINT = 'Not in your SWISS plan — contact My Safe Spaces to upgrade';

const SubRail = ({ section }) => {
  const { school } = useAuth();

  if (!section || !section.items || section.items.length === 0) return null;

  const visible = section.items.filter(
    (item) => !(item.requiresAssessments && !school?.assessmentsEnabled)
  );

  if (!visible.length) return null;

  const renderLink = (item, variant) => {
    const locked = !!item.module && !schoolHasModule(school, item.module);
    return (
      <NavLink
        key={item.path}
        to={item.path}
        end={item.exact}
        title={locked ? LOCKED_HINT : undefined}
        className={({ isActive }) =>
          [variant, isActive ? 'active' : '', locked ? 'locked' : '']
            .filter(Boolean)
            .join(' ')
        }
      >
        <span className="subrail-label">{item.label}</span>
        {locked && <Lock size={12} className="subrail-lock" aria-hidden="true" />}
      </NavLink>
    );
  };

  return (
    <>
      <aside className="subrail" aria-label={`${section.label} pages`}>
        <div className="subrail-heading">{section.label}</div>
        <nav className="subrail-nav">
          {visible.map((item) => renderLink(item, 'subrail-link'))}
        </nav>
      </aside>

      <nav className="subrail-chips" aria-label={`${section.label} pages`}>
        {visible.map((item) => renderLink(item, 'subrail-chip'))}
      </nav>
    </>
  );
};

export default SubRail;
